// src/services/article.ts
import api from "../lib/api";

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface ArticleAuthor {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
  title?: string;
  organizationName?: string;
}

export interface Article {
  id: string;
  title: string;
  summary: string;
  content: string[];
  date: string;
  author: string;
  authorId?: string;
  authorUser?: ArticleAuthor;
  industry: string;
  industryId: string;
  keyPoints: string[];
  conclusion: string;
  tags: string[];
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Innovation {
  id: string;
  title: string;
  summary: string;
  content: string[];
  date: string;
  author: string;
  authorId?: string;
  authorUser?: ArticleAuthor;
  industry: string;
  industryId: string;
  keyPoints: string[];
  conclusion: string;
  tags: string[];
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ArticleCreateData {
  title: string;
  summary: string;
  content: string[];
  industry: string;
  industryId: string;
  keyPoints: string[];
  conclusion: string;
  tags: string[];
  date?: string;
}

// ============================================
// PUBLIC ARTICLE ENDPOINTS
// ============================================

/**
 * Get all approved articles (Public - no auth required)
 */
export async function getArticles(filters?: {
  industry?: string;
  search?: string;
}): Promise<Article[]> {
  const params = new URLSearchParams();
  if (filters?.industry) params.append('industry', filters.industry);
  if (filters?.search) params.append('search', filters.search);

  const { data } = await api.get(`/api/content/articles?${params.toString()}`);
  return data.data.articles;
}

/**
 * Get a single approved article by ID (Public - no auth required)
 */
export async function getArticleById(id: string): Promise<Article> {
  const { data } = await api.get(`/api/content/articles/${id}`);
  return data.data.article;
}

// ============================================
// PUBLIC INNOVATION ENDPOINTS
// ============================================

/**
 * Get all approved innovations (Public - no auth required)
 */
export async function getInnovations(filters?: {
  industry?: string;
  search?: string;
}): Promise<Innovation[]> {
  const params = new URLSearchParams();
  if (filters?.industry) params.append('industry', filters.industry);
  if (filters?.search) params.append('search', filters.search);

  const { data } = await api.get(`/api/content/innovations?${params.toString()}`);
  return data.data.innovations;
}

/**
 * Get a single approved innovation by ID (Public - no auth required)
 */
export async function getInnovationById(id: string): Promise<Innovation> {
  const { data } = await api.get(`/api/content/innovations/${id}`);
  return data.data.innovation;
}

// ============================================
// EXPERT ENDPOINTS
// ============================================

/**
 * Create a new article (Expert only)
 */
export async function createArticle(articleData: ArticleCreateData): Promise<Article> {
  const { data } = await api.post('/api/content/articles', articleData);
  return data.data.article;
}

/**
 * Create a new innovation (Expert only)
 */
export async function createInnovation(innovationData: ArticleCreateData): Promise<Innovation> {
  const { data } = await api.post('/api/content/innovations', innovationData);
  return data.data.innovation;
}

/**
 * Get my articles (Expert only)
 */
export async function getMyArticles(): Promise<Article[]> {
  const { data } = await api.get('/api/content/my/articles');
  return data.data.articles;
}

/**
 * Get my innovations (Expert only)
 */
export async function getMyInnovations(): Promise<Innovation[]> {
  const { data } = await api.get('/api/content/my/innovations');
  return data.data.innovations;
}

// ============================================
// ADMIN ENDPOINTS
// ============================================

/**
 * Get all articles (Admin only)
 */
export async function getAllArticles(filters?: {
  status?: 'pending' | 'approved' | 'rejected';
}): Promise<Article[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);

  const { data } = await api.get(`/api/content/admin/articles?${params.toString()}`);
  return data.data.articles;
}

/**
 * Get all innovations (Admin only)
 */
export async function getAllInnovations(filters?: {
  status?: 'pending' | 'approved' | 'rejected';
}): Promise<Innovation[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);

  const { data } = await api.get(`/api/content/admin/innovations?${params.toString()}`);
  return data.data.innovations;
}

/**
 * Approve an article (Admin only)
 */
export async function approveArticle(id: string): Promise<Article> {
  const { data } = await api.patch(`/api/content/admin/articles/${id}/approve`);
  return data.data.article;
}

/**
 * Approve an innovation (Admin only)
 */
export async function approveInnovation(id: string): Promise<Innovation> {
  const { data } = await api.patch(`/api/content/admin/innovations/${id}/approve`);
  return data.data.innovation;
}

/**
 * Reject an article (Admin only)
 */
export async function rejectArticle(id: string, reason?: string): Promise<Article> {
  const { data } = await api.patch(`/api/content/admin/articles/${id}/reject`, { reason });
  return data.data.article;
}

/**
 * Reject an innovation (Admin only)
 */
export async function rejectInnovation(id: string, reason?: string): Promise<Innovation> {
  const { data } = await api.patch(`/api/content/admin/innovations/${id}/reject`, { reason });
  return data.data.innovation;
}

/**
 * Delete an article (Admin only)
 */
export async function deleteArticle(id: string): Promise<void> {
  await api.delete(`/api/content/admin/articles/${id}`);
}

/**
 * Delete an innovation (Admin only)
 */
export async function deleteInnovation(id: string): Promise<void> {
  await api.delete(`/api/content/admin/innovations/${id}`);
}
